import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Star, Building2, Eye, CheckCircle2, MapPin, ShieldCheck, Globe, Compass, Users, TimerReset, Package2, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ListingCard } from "@/components/ListingCard";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { HeroKuulaTour } from "@/components/HeroKuulaTour";
import { HeroTourContext } from "@/components/HeroTourContext";
import { BetaPricing } from "@/components/BetaPricing";
import { Testimonials } from "@/components/Testimonials";
import { fetchApprovedListings } from "@/lib/listings";
import type { PropertyListing } from "@/data/sample-listings";
import { BusinessSolutions } from "@/components/BusinessSolutions";
import { FeaturedToursSection } from "@/components/FeaturedToursSection";
import { productSpinItems, showroomExperienceItems } from "@/data/product-showcase";
import { usePageSeo } from "@/lib/seo";

const features = [
  {
    icon: Eye,
    title: "Immersive 360° Tours",
    description: "Walk through properties from anywhere. Our high-definition virtual tours let you explore every corner before visiting."
  },
  {
    icon: Building2,
    title: "Premium Venues",
    description: "Curated selection of Nigeria's finest event centres, hotels, and apartments. Only the best make it to Vista360."
  },
  {
    icon: Star,
    title: "Verified Listings",
    description: "Every property is personally verified by our team. What you see is exactly what you get."
  },
];

const registryFeatures = [
  {
    icon: ShieldCheck,
    title: "Shadow Registry",
    subtitle: "Visual Tenure Security",
    description: "Every 8K tour is timestamped and geotagged, creating an auditable visual trail that paper titles can't match. We combat the ₦36 Trillion lost annually to untitled and disputed land."
  },
  {
    icon: Globe,
    title: "Vista Street View",
    subtitle: "Mapping the Unmapped",
    description: "New Mega Estates along Airport Road and Katampe don't exist on Google Maps yet. We map them in 8K so Diaspora buyers can 'drive' through streets before Google ever arrives."
  },
  {
    icon: Compass,
    title: "Drone-to-Door Navigation",
    subtitle: "Solving the Address Gap",
    description: "Nigerian addresses are notoriously hard to find. Our 360° Hotspots create Visual Wayfinding with nearby landmarks, providing spatial context Google Maps currently lacks."
  },
  {
    icon: MapPin,
    title: "Geotag Verification",
    subtitle: "Source of Truth",
    description: "Every listing displays exact GPS coordinates and beacon numbers extracted from 8K capture metadata — a Visual Certificate of Occupancy for investors and banks."
  },
];

const revenueFunnels = [
  {
    icon: Users,
    title: "For Agents",
    description: "Sell and rent faster with a complete media package: 360 tour, edited photos, and a short video reel.",
    cta: "Explore Agent Packages",
    href: "/agents",
  },
  {
    icon: Building2,
    title: "For Developers",
    description: "Help estates launch with real visual proof instead of flyer renders, and give external agents better sales material.",
    cta: "See Developer Packages",
    href: "/developers",
  },
  {
    icon: TimerReset,
    title: "Progress Tracking",
    description: "Offer independent site monitoring with recurring photos, 360 captures, and timeline updates for projects in motion.",
    cta: "View Tracking Service",
    href: "/progress-tracking",
  },
];

const Index = () => {
  usePageSeo({
    title: "Virtual Tours Nigeria",
    description: "Explore verified 360 virtual tours of properties, venues, and premium spaces across Nigeria with Vista360 NG.",
    path: "/",
  });

  const [featuredListings, setFeaturedListings] = useState<PropertyListing[]>([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  useEffect(() => {
    setLoadingFeatured(true);
    fetchApprovedListings()
      .then((listings) => setFeaturedListings(listings.filter((l) => l.featured).slice(0, 3)))
      .finally(() => setLoadingFeatured(false));
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Navbar />
      
      {/* Hero 360 Banner - Full Screen Immersive Tour */}
      <HeroKuulaTour />

      <HeroTourContext />

      {/* Why Vista360 */}
      <section className="section-padding bg-muted">
        <div className="container-tight">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-primary font-semibold text-sm uppercase tracking-wider"
            >
              Why Vista360 Works
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-2"
            >
              Explore Real Spaces Before You Move
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-6 shadow-md border border-border"
              >
                <div className="w-12 h-12 rounded-xl hero-gradient flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="font-display font-bold text-xl text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Private GIS / Shadow Registry Section */}
      <section className="section-padding bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 pattern-grid-secondary" />
        </div>
        <div className="container-tight relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-secondary font-semibold text-sm uppercase tracking-wider"
            >
              Nigeria's Private Proprietary GIS
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-background mt-3 mb-4"
            >
              We Hold the Map
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-background/70 text-lg"
            >
              In developed markets, Google has already mapped everything. In Nigeria, Vista360 is building a{" "}
              <span className="text-secondary font-semibold">Verified Visual Registry</span> — filling the ₦36 Trillion gap in untitled and unmapped real estate.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {registryFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 border border-background/10"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl gold-gradient flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-foreground" />
                  </div>
                  <div>
                    <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-1">{feature.subtitle}</p>
                    <h3 className="font-display font-bold text-xl text-background mb-2">{feature.title}</h3>
                    <p className="text-background/60 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-10 text-center"
          >
            <Link to="/about">
              <Button size="lg" className="gold-gradient text-foreground font-semibold shadow-gold">
                Learn About Our Vision
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Revenue Funnels */}
      <section className="section-padding bg-background">
        <div className="container-tight">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-primary font-semibold text-sm uppercase tracking-wider"
            >
              Built To Convert
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-2 mb-4"
            >
              Three Fast-Moving Service Funnels
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-muted-foreground text-lg"
            >
              These are the offers most likely to generate immediate revenue right now: agent packages, developer media packages, and recurring progress monitoring.
            </motion.p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {revenueFunnels.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-card rounded-2xl p-8 shadow-md border border-border"
              >
                <div className="w-14 h-14 rounded-2xl hero-gradient flex items-center justify-center mb-5">
                  <item.icon className="w-7 h-7 text-primary-foreground" />
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground mb-3">{item.title}</h3>
                <p className="text-muted-foreground mb-6">{item.description}</p>
                <Link to={item.href}>
                  <Button className="w-full" size="lg">
                    {item.cta}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Solutions */}
      <BusinessSolutions />

      {/* Product Photography Teaser */}
      <section className="section-padding bg-background">
        <div className="container-tight">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-center rounded-[32px] border border-border bg-card p-8 lg:p-10 shadow-premium overflow-hidden">
            <div>
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">New Revenue Line</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-2 mb-4">
                Add 360 product spins without disturbing the live tours marketplace
              </h2>
              <p className="text-muted-foreground text-lg mb-6">
                Vista360 now has a dedicated service surface for featured products, retail catalog spins, and immersive showroom capture. Products use a spin-style viewer. Spaces keep using our proven panoramic tour flow.
              </p>
              <div className="space-y-3 mb-8 text-sm text-muted-foreground">
                <p className="flex items-start gap-3"><Package2 className="mt-0.5 h-4 w-4 text-primary flex-shrink-0" /> Placeholder product spin demos are isolated from listing categories and search filters.</p>
                <p className="flex items-start gap-3"><Store className="mt-0.5 h-4 w-4 text-primary flex-shrink-0" /> Real showroom walkthroughs reuse the existing Vista360 immersive tour stack.</p>
              </div>
              <Link to="/product-photography">
                <Button size="lg">
                  Explore Product Photography
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-3xl border border-border bg-muted/50 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-foreground">
                  <img src={productSpinItems[0]?.posterImage} alt={productSpinItems[0]?.title} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-5">
                  <span className="badge-360 text-xs mb-3 inline-block">Product Spin Demo</span>
                  <h3 className="font-display text-xl font-bold text-foreground">{productSpinItems[0]?.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Interactive placeholder spin experience for commerce-ready products.</p>
                </div>
              </div>

              <div className="rounded-3xl border border-border bg-muted/50 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-foreground">
                  <img src={showroomExperienceItems[0]?.imageUrl} alt={showroomExperienceItems[0]?.title} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-5">
                  <span className="badge-360 text-xs mb-3 inline-block">Showroom Tour</span>
                  <h3 className="font-display text-xl font-bold text-foreground">{showroomExperienceItems[0]?.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Immersive panoramic capture for premium environments and sales spaces.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured 360° Tours */}
      <FeaturedToursSection />

      {/* Featured Listings */}
      <section className="section-padding bg-background">
        <div className="container-tight">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10">
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-primary font-semibold text-sm uppercase tracking-wider"
              >
                Featured Properties
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-2"
              >
                Top Venues in Abuja
              </motion.h2>
            </div>
            <Link to="/tours?city=abuja">
              <Button variant="outline" className="mt-4 sm:mt-0">
                View All <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loadingFeatured ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-muted-foreground">Loading featured properties…</p>
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {featuredListings.map((listing, index) => (
                <ListingCard key={listing.id} listing={listing} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No featured properties available yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Beta Pricing */}
      <BetaPricing />

      {/* Testimonials */}
      <Testimonials />

      {/* CTA Section */}
      <section className="section-padding hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-plus-white" />
        </div>
        
        <div className="container-tight relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6"
            >
              Ready to Showcase Your Property?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-primary-foreground/80 text-lg mb-8"
            >
              Join 50+ venues already using Vista360 to attract more customers. 
              Get your virtual tour created in just 48 hours.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/pricing">
                <Button size="lg" className="gold-gradient text-foreground font-semibold shadow-gold w-full sm:w-auto">
                  View Pricing
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <WhatsAppButton 
                message="Hello! I'm interested in getting a 360° virtual tour for my property. Can you tell me more about your services?"
                label="Chat with Us"
              />
            </motion.div>
          </div>
        </div>
      </section>


      <Footer />
      
      {/* Floating WhatsApp Button */}
      <WhatsAppButton 
        variant="floating"
        message="Hello! I'd like to learn more about Vista360 virtual tours."
      />
    </div>
  );
};

export default Index;
